/**
 * `/v1/domains` — the request shapes (§5, §7).
 *
 * One transport-neutral op behind two envelopes, so nothing here knows about Hono or tRPC.
 *
 * **The grammar is not spelled here.** `HOSTNAME` is derived from `SQL_PATTERNS.hostname`,
 * which is the text `0023`'s CHECK carries, so this schema and the database hold one rule
 * — `0016`'s own instruction for the day the validator arrived.
 */
import { z } from "zod";
import { DOMAIN_KINDS } from "../serving.js";
import { HOSTNAME } from "./patterns.js";
const HOSTNAME_MESSAGE = "That is not a hostname we can serve. It needs at least two labels — " +
    "`id.yourbrand.com` — using only letters, digits and hyphens, and no label may start " +
    "or end with a hyphen.";
const KIND_MESSAGE = `kind must be one of: ${DOMAIN_KINDS.join(", ")}.`;
/**
 * Trim, lowercase, and drop one trailing dot.
 *
 * The fully-qualified form `id.brand.com.` is what a DNS tool prints and what a careful
 * person pastes; it names the same host. `0016`'s CHECK stores lowercase only, so the
 * canonicalisation happens before validation rather than being refused after it — the API
 * owns the grammar, the database guarantees the case.
 */
function canonicalise(raw) {
    const trimmed = raw.trim().toLowerCase();
    return trimmed.endsWith(".") ? trimmed.slice(0, -1) : trimmed;
}
/**
 * The tier adding a hostname is included from — `screens.md` §8's state E. Serving is never
 * gated: a domain already in service keeps answering on any plan (Q-P4).
 */
export const CUSTOM_DOMAIN_TIER = "starter";
/**
 * `POST /v1/domains` — claim a hostname.
 *
 * **An apex is accepted here**, and the screen steers rather than refuses: §8.1 blocks only
 * what is unrecoverable after printing, and pointing an apex at us is undone by editing one
 * DNS record. Refusing it at the schema would make the steer unreachable.
 */
export const createDomainSchema = z
    .object({
    hostname: z
        .string(HOSTNAME_MESSAGE)
        .transform(canonicalise)
        .refine((host) => host.length >= 4 && host.length <= 253, HOSTNAME_MESSAGE)
        .refine((host) => HOSTNAME.test(host), HOSTNAME_MESSAGE),
    /**
     * Which delegation this is. Defaulted to `resolver` because that is the one the
     * product sells — the hostname printed codes resolve through — and `pip` is a
     * separate question a tenant reaches later.
     */
    kind: z.enum(DOMAIN_KINDS, KIND_MESSAGE).default("resolver"),
})
    .describe("POST /v1/domains — claim a hostname and get its DNS instructions.");
/** `POST /v1/domains/{id}/recheck` — ask DNS again, now. */
export const recheckDomainSchema = z
    .object({ id: z.uuid() })
    .describe("POST /v1/domains/{id}/recheck — look up the record again.");
/** `DELETE /v1/domains/{id}` — take a hostname out of service. */
export const removeDomainSchema = z
    .object({ id: z.uuid() })
    .describe("DELETE /v1/domains/{id} — stop serving this hostname.");
//# sourceMappingURL=domains.js.map